/**
 * WebSocket (STOMP) клиент для получения прогресса конвертации видео.
 * Бэкенд рассылает сообщения в /topic/video-conversion/{videoId}.
 * Payload: { videoId, percent, status: 'PROCESSING'|'READY'|'FAILED', message? }
 */
import { Client } from '@stomp/stompjs';
import { Preferences } from '@capacitor/preferences';

const API_BASE = process.env.VUE_APP_API_URL || '';
const WS_URL = API_BASE.replace(/^http/i, 'ws').replace(/\/$/, '') + '/websocket';

let client: Client | null = null;
let connecting: Promise<Client | null> | null = null;
const subscriptions = new Map<number, () => void>();
const callbacks = new Map<number, (data: { videoId: number; percent: number; status: string; message?: string }) => void>();

async function getAuthToken(): Promise<string | null> {
  const { value: jwt } = await Preferences.get({ key: 'jwt' });
  const webJwt = (
    localStorage.getItem('jhi-authenticationToken') ||
    sessionStorage.getItem('jhi-authenticationToken') ||
    ''
  ).replace(/^"|"$/g, '');
  return jwt || webJwt || null;
}

function subscribeTopic(c: Client, videoId: number): void {
  const sub = c.subscribe(`/topic/video-conversion/${videoId}`, msg => {
    try {
      if (!msg.body) return;
      const data = JSON.parse(msg.body);
      const cb = callbacks.get(videoId);
      if (cb) cb(data);
    } catch (err) {
      console.warn('Failed to parse conversion progress message:', err);
    }
  });
  subscriptions.set(videoId, () => sub.unsubscribe());
}

async function getOrCreateClient(): Promise<Client | null> {
  if (client?.active) return client;
  if (connecting) return connecting;

  connecting = (async () => {
    const token = await getAuthToken();
    if (!token) {
      console.warn('Video conversion WebSocket: нет JWT');
      return null;
    }
    client = new Client({
      brokerURL: WS_URL,
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      debug: str => console.log('[STOMP]', str),
      onConnect: () => {
        const c = client;
        if (!c) return;
        subscriptions.forEach((_unsub, videoId) => {
          subscribeTopic(c, videoId);
        });
      },
      onStompError: frame => {
        console.warn('Video conversion WebSocket STOMP error:', frame);
      },
    });
    client.activate();
    return client;
  })();

  try {
    return await connecting;
  } finally {
    connecting = null;
  }
}

export function subscribeToConversionProgress(
  videoId: number,
  onProgress: (data: { videoId: number; percent: number; status: string; message?: string }) => void
): () => void {
  callbacks.set(videoId, onProgress);
  void getOrCreateClient().then(c => {
    if (!c) return;
    if (c.connected) {
      subscribeTopic(c, videoId);
    } else if (!subscriptions.has(videoId)) {
      subscriptions.set(videoId, () => {});
    }
  });
  return () => {
    const unsub = subscriptions.get(videoId);
    if (unsub) unsub();
    subscriptions.delete(videoId);
    callbacks.delete(videoId);
  };
}

export function disconnect(): void {
  if (client) {
    void client.deactivate();
    client = null;
  }
  connecting = null;
  subscriptions.clear();
  callbacks.clear();
}
